import i18next from 'i18next';
import {Observable} from 'rxjs';
import {getFormValues, change} from 'redux-form';

import {combineEpics} from 'web-app/util/redux-observable';
import FilesApi from 'web-app/api/files-api';
import {showDeleteDialogObservable} from 'web-app/react/dialog';
import * as FileActions from 'web-app/react/components/file-upload/actions';
import * as GlobalEventActions from 'web-app/react/global-event/actions';

import * as Actions from './actions';
import * as Constants from './constants';
import {type FormData} from './add-document/add-document-form';

const fetchFilesObservable = (payload, success, error) =>
    FilesApi.fetchFoldersAndFiles(payload.institutionId, payload.folderId)
        .map(result => success.action(payload.institutionId, payload.folderId, result))
        .catch(e => Observable.of(error.action(e)));

const fetchFoldersAndFiles = action$ =>
    action$.ofType(Actions.fetchData.type).mergeMap(({payload}) => {
        return fetchFilesObservable(payload, Actions.fetchDataSuccess, Actions.fetchDataFailed);
    });

const getFolderStructure = action$ =>
    action$.ofType(Actions.fetchStructure.type).mergeMap(({payload}) => {
        return fetchFilesObservable(payload, Actions.fetchStructureSuccess, Actions.fetchStructureFailed);
    });

const moveFile = action$ =>
    action$.ofType(Actions.moveFile.type).mergeMap(({payload}) =>
        FilesApi.updateFile(
            payload.institutionId,
            payload.filePersistenceId,
            payload.folderId === null ? 'null' : payload.folderId,
        ) // API interprets 'null' as moving to the top folder.
            .flatMap(() => [
                Actions.moveFileSuccess.action(),
                GlobalEventActions.updateSuccess.action(i18next.t('documents.moveDocumentSuccess')),
            ])
            .do(() => {
                if (payload.url) {
                    window.location.hash = payload.url;
                }
            })
            .catch(e => Observable.of(Actions.moveFileFailed.action(e))),
    );

const deleteDocument = action$ =>
    action$.ofType(Actions.deleteFile.type).mergeMap(({payload}) =>
        showDeleteDialogObservable({
            title: '',
            description: i18next.t('documents.deleteFileConfirm'),
            type: 'mf',
        }).mergeMap(selection => {
            if (selection === 'OK') {
                return FilesApi.deleteFile(payload.filePersistenceId)
                    .flatMap(() => [
                        Actions.deleteFileSuccess.action(payload.filePersistenceId, payload.documentAge),
                        GlobalEventActions.updateSuccess.action(i18next.t('documents.documentDeleteSuccess')),
                    ])
                    .catch(e => Observable.of(Actions.deleteFileFailed.action(e)));
            }
            return Observable.of(Actions.deleteFileAborted.action());
        }),
    );

const createFolder = action$ =>
    action$.ofType(Actions.createFolder.type).mergeMap(({payload}) =>
        FilesApi.createFolder(payload.institutionId, payload.title, payload.parent)
            .flatMap(result => [
                Actions.createFolderSuccess.action(payload.institutionId, payload.title, payload.parent, result),
                GlobalEventActions.updateSuccess.action(i18next.t('documents.createFolderSuccess')),
            ])
            .catch(e => Observable.of(Actions.createFolderFailed.action(e))),
    );

const renameFolder = action$ =>
    action$.ofType(Actions.renameFolder.type).mergeMap(({payload}) =>
        FilesApi.changeFolderName(payload.institutionId, payload.folderId, payload.title)
            .flatMap(result => [
                Actions.renameFolderSuccess.action(payload.institutionId, payload.folderId, payload.title, result),
                GlobalEventActions.updateSuccess.action(i18next.t('documents.renameFolderSuccess')),
            ])
            .catch(e => Observable.of(Actions.renameFolderFailed.action(e))),
    );

const deleteFolder = action$ =>
    action$.ofType(Actions.deleteFolder.type).mergeMap(({payload}) =>
        showDeleteDialogObservable({
            title: '',
            description: i18next.t('documents.deleteFolderConfirm'),
            type: 'mf',
        }).mergeMap(selection => {
            if (selection === 'OK') {
                return FilesApi.deleteFolder(payload.institutionId, payload.folderId)
                    .flatMap(() => [
                        Actions.deleteFolderSuccess.action(payload.institutionId, payload.folderId),
                        GlobalEventActions.updateSuccess.action(i18next.t('documents.deleteFolderSuccess')),
                    ])
                    .catch(e => Observable.of(Actions.deleteFolderFailed.action(e)));
            }
            return Observable.of(Actions.deleteFolderAborted.action());
        }),
    );

const fileAdded = (action$, state$) =>
    action$
        .ofType(Actions.fileAdded.type)
        .filter(({payload}) => {
            const formData = getFormValues(Constants.ADD_DOCUMENT_FORM_NAME)(state$.value) as FormData | undefined;
            return payload.file && payload.file.name && (!formData || !formData.title);
        })
        .mergeMap(({payload}) => Observable.of(change(Constants.ADD_DOCUMENT_FORM_NAME, 'title', payload.file.name)));

const createFile = action$ =>
    action$.ofType(Actions.createFile.type).mergeMap(({payload}) =>
        FilesApi.create(payload.institutionId, payload.fileId, payload.title, payload.description, payload.folderId)
            .flatMap(result => [
                Actions.createFileSuccess.action(result),
                FileActions.clearFiles(Constants.ADD_DOCUMENT_FORM_NAME),
                GlobalEventActions.updateSuccess.action(i18next.t('documents.createDocumentSuccess')),
            ])
            .catch(e => Observable.of(Actions.createFileFailed.action(e))),
    );

const renameFile = action$ =>
    action$.ofType(Actions.renameFile.type).mergeMap(({payload}) =>
        FilesApi.updateFile(payload.institutionId, payload.filePersistenceId, null, payload.title, payload.description)
            .flatMap(result => [
                Actions.renameFileSuccess.action(
                    payload.institutionId,
                    payload.filePersistenceId,
                    payload.title,
                    payload.description,
                    result,
                ),
                GlobalEventActions.updateSuccess.action(i18next.t('documents.renameDocumentSuccess')),
            ])
            .catch(e => Observable.of(Actions.renameFileFailed.action(e))),
    );

export default combineEpics(
    fetchFoldersAndFiles,
    getFolderStructure,
    moveFile,
    deleteDocument,
    createFolder,
    renameFolder,
    deleteFolder,
    fileAdded,
    createFile,
    renameFile,
);
